import React, { useEffect, useState } from 'react';
import { bool, func, number, object, shape, string } from 'prop-types';
import { reportToSegment, types, eventNames } from '@smartcar/morse';
import { Box, Tab, Tabs, Typography } from '@mui/material';

import { TabsNav } from '../../../../../../global-styles/components';
import { InlineLinks, Modal, Spinner } from '../../../../../../components';
import { modalConfig } from '../../utils/webhookModalHelpers';
import { TestPanel } from './components';

const modalName = 'test';

export const staticText = {
  chip: 'POST',
  request: 'Request',
  documentation: 'This will send the payloads below to your callback URI and optional error callback URI. See our |+|Testing guide@@https://smartcar.com/docs/api-reference/webhooks/callback-verification|+| to learn more.',
  buttons: {
    dataTest: 'Send data test request',
    errorTest: 'Send error test request',
  },
};

const TestWebhookModal = ({
  actions: {
    fetchTestEventRequest,
    testWebhookRequest,
    toggleWebhookModal,
    clearTestResponse,
  },
  webhooks,
  currentWebhook,
  testEvent,
  testWebhookResponse,
  isTestingWebhook,
}) => {
  const [currentTab, setCurrentTab] = useState(0);
  const sampleDataTested = testWebhookResponse && testWebhookResponse.data;
  const sampleErrorTested = testWebhookResponse && testWebhookResponse.error;

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  useEffect(() => {
    fetchTestEventRequest(currentWebhook);

    return () => {
      clearTestResponse();
    };
  }, []);

  const { callbackUri, errorCallbackUri, version } = webhooks[currentWebhook];

  const toggleModal = (trackCloseEvent = true) => {
    toggleWebhookModal({ nextModal: null, curModal: modalName, trackCloseEvent });
  };

  const testWebhook = (type) => {
    // v4 data and error test requests are handled as separate calls
    const sampleDataPayload = version === '4.0' ? testEvent.sampleData : testEvent;
    testWebhookRequest(currentWebhook, {
      payload: type === 'error' ? testEvent.sampleError : sampleDataPayload,
      type: type || 'data',
    });
    reportToSegment(types.TRACK, eventNames.buttonClicked, { label: 'backend action', text: type === 'error' ? staticText.buttons.errorTest : staticText.buttons.dataTest });
  };

  const content = (
    <Box display="flex" flexDirection="column">
      <Typography variant="body2" marginBottom={2}><InlineLinks text={staticText.documentation} /></Typography>
      <TabsNav color="transparent" component="nav" elevation={0}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
        >
          <Tab label="Data" value={0} />
          {testEvent && testEvent.sampleError && <Tab label="Error" value={1} />}
        </Tabs>
      </TabsNav>
      {testEvent ? (
        <div>
          <TestPanel
            index={0}
            currentTab={currentTab}
            callbackUri={callbackUri}
            testPayload={version === '4.0' ? testEvent.sampleData : testEvent}
            tested={sampleDataTested}
            response={testWebhookResponse && testWebhookResponse.data}
            isTestingWebhook={isTestingWebhook}
            testWebhook={testWebhook}
            toggleModal={toggleModal}
          />
          {testEvent.sampleError && (
            <TestPanel
              index={1}
              currentTab={currentTab}
              callbackUri={errorCallbackUri || callbackUri}
              testPayload={testEvent.sampleError}
              tested={sampleErrorTested}
              response={testWebhookResponse && testWebhookResponse.error}
              isTestingWebhook={isTestingWebhook}
              testWebhook={testWebhook}
              toggleModal={toggleModal}
              type="error"
            />
          )}
        </div>
      ) : (
        <Spinner />
      )}
    </Box>
  );

  return (
    <Modal title={modalConfig[modalName].title} content={content} toggleModal={toggleModal} />
  );
};

export default TestWebhookModal;

TestWebhookModal.propTypes = {
  webhooks: object.isRequired,
  currentWebhook: string.isRequired,
  testEvent: shape({
    sampleData: object,
    sampleError: object,
  }),
  testWebhookResponse: shape({
    data: shape({
      body: object.isRequired,
      statusCode: number.isRequired,
    }),
    error: shape({
      body: object.isRequired,
      statusCode: number.isRequired,
    }),
  }),
  isTestingWebhook: bool.isRequired,
  actions: shape({
    fetchTestEventRequest: func.isRequired,
    testWebhookRequest: func.isRequired,
    clearTestResponse: func.isRequired,
    toggleWebhookModal: func.isRequired,
  }).isRequired,
};

TestWebhookModal.defaultProps = {
  testEvent: null,
  testWebhookResponse: null,
};
